import Alert from 'antd/lib/alert';
import Card from 'antd/lib/card/Card';
import Empty from 'antd/lib/empty';
import Typography from 'antd/lib/typography';
import { add } from 'date-fns';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart, LineChart } from 'echarts/charts';
import { DataZoomComponent, GridComponent, LegendScrollComponent, MarkAreaComponent, TooltipComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { isNumber } from 'lodash';
import { useMemo } from 'react';
import {
    EnergyEventDto,
    EnergyUsageTimeseriesDto,
    getBaselineCaiso10d,
    getBaselineStatic,
    getPerformanceCaiso10d,
    getPerformanceStatic,
    getTimestamp,
} from '../../../../domain/energy/interface';
import { SECONDARY_COLOR, PRIMARY_COLOR } from '../../../../theme';
import { IDateRange } from '../../../interface';
import { EChartsOption } from 'echarts-for-react';
import { renderTooltip } from './chartUtils';
import { BaselineMode } from '../../../../domain/energy/baselineMarket';

echarts.use([BarChart, LineChart, TooltipComponent, GridComponent, CanvasRenderer, MarkAreaComponent, DataZoomComponent, LegendScrollComponent]);

type MarkAreaType = [{ xAxis: Date | number; itemStyle: { color: string } }, { xAxis: Date | number }];

const buildMarkAreaItem = (start: Date | number, end: Date | number, color: string): MarkAreaType => {
    return [{ xAxis: start, itemStyle: { color } }, { xAxis: end }];
};

function makeEventChartData(eventsData: EnergyEventDto[]): MarkAreaType[] {
    if (!eventsData?.length) {
        return [];
    }

    const markAreaList: MarkAreaType[] = eventsData.map(event => {
        const start = new Date(event.start);
        const end = add(new Date(event.start), { minutes: event.duration });
        return buildMarkAreaItem(start, end, '#F1D5C7');
    });

    return markAreaList;
}

interface IEnergyDemandWidget {
    dateRange: IDateRange;
    energyData: EnergyUsageTimeseriesDto; // aggregate
    eventsData: EnergyEventDto[]; // aggregate
    /** which baseline/performance to render based on the selected sites' markets; 'none' hides them */
    baselineMode: BaselineMode;
    /** warning shown when baseline/performance can't be aggregated across markets */
    baselineWarning?: string;
}

export const EnergyDemandWidget = ({ dateRange, energyData, eventsData, baselineMode, baselineWarning }: IEnergyDemandWidget) => {
    const isDataExist = Boolean(energyData?.values?.length);
    const showBaseline = baselineMode !== 'none';

    const getBaseline = baselineMode === 'static' ? getBaselineStatic : getBaselineCaiso10d;
    const getPerformance = baselineMode === 'static' ? getPerformanceStatic : getPerformanceCaiso10d;

    const preparedEnergyData = useMemo(() => {
        return energyData?.values.map(([timestamp, usageKw]) => {
            return { value: [timestamp, usageKw], itemStyle: { color: PRIMARY_COLOR } };
        });
    }, [energyData]);

    const baselineChartData = useMemo(() => {
        return energyData?.values.map(val => {
            return {
                value: [getTimestamp(val), getBaseline(val)],
            };
        });
    }, [energyData, getBaseline]);

    const performanceChartData: [number, number | null][] = useMemo(() => {
        const eventsMap = makeEventsMap(eventsData);

        const allPerformance = energyData?.values.map(val => {
            const timestamp = getTimestamp(val);
            const performance = getPerformance(val);

            /** attach info about events */
            const eventTs = eventsMap.get(timestamp) ?? null;

            /** we don't want to show negative performance */
            const noNegativePerformance = isNumber(performance) && performance < 0 ? 0 : performance;

            return [timestamp, noNegativePerformance, eventTs];
        }) as [number, number | null, EventTs | null][];

        const eventPerformance: [number, number | null, EventTs | null][] = [];

        let prevIsIncluded = false;
        allPerformance.forEach((item, index) => {
            const [ts, , eventTs] = item;

            if (eventTs) {
                if (!prevIsIncluded && index > 0) {
                    eventPerformance.push(allPerformance[index - 1]);
                }
                eventPerformance.push(item);
                prevIsIncluded = true;
            } else {
                if (prevIsIncluded && index < allPerformance.length - 1) {
                    eventPerformance.push(item);
                } else {
                    /**
                     * here we include timestamp into chart data but set value to null
                     * - so it is not displayed,
                     * this is needed to prevent EChart lib connecting dots
                     */
                    eventPerformance.push([ts, null, eventTs]);
                }
                prevIsIncluded = false;
            }
        });

        return eventPerformance.map(it => [it[0], it[1]]);
    }, [energyData, eventsData, getPerformance]);

    const eventChartData = makeEventChartData(eventsData);

    const option: EChartsOption = {
        useUTC: true,
        legend: {},
        tooltip: {
            trigger: 'axis',
            axisPointer: {
                type: 'line',
                snap: true,
                lineStyle: {
                    type: 'solid',
                },
            },
            formatter: (params: any) => {
                return renderTooltip(params);
            },
        },
        grid: {
            bottom: '7%',
            right: '10%',
        },
        xAxis: {
            type: 'time',
            axisLabel: {
                color: '#697475',
                margin: 10,
                formatter: {
                    year: '{yyyy}',
                    month: '{MMM}',
                    day: '{dd}-{MMM}',
                    hour: '{HH}:{mm}',
                    minute: '{HH}:{mm}',
                },
            },
            axisLine: {
                show: true,
                lineStyle: {
                    color: '#d6dbdb',
                    width: 2,
                    type: 'solid',
                },
            },
            axisTick: {
                show: true,
            },
            boundaryGap: true,
            max: dateRange.end.valueOf(),
            min: dateRange.start.valueOf(),
        },
        yAxis: [
            {
                type: 'value',
                max: (value: any) => {
                    return Math.round(value.max * 1.1);
                },
                name: 'kW',
                nameLocation: 'middle',
                nameGap: 40,
                position: 'left',
                alignTicks: true,
            },
        ],
        series: [
            {
                type: 'bar',
                name: 'Usage (kW)',
                legend: {
                    itemStyle: {
                        color: PRIMARY_COLOR,
                    },
                },
                showSymbol: false,
                data: preparedEnergyData,
                itemStyle: {
                    color: PRIMARY_COLOR,
                },
            },
            ...(showBaseline
                ? [
                      {
                          type: 'line',
                          smooth: true,
                          name: 'Estimated Baseline',
                          data: baselineChartData,
                          itemStyle: {
                              color: '#4A7088',
                          },
                      },
                      {
                          type: 'line',
                          smooth: true,
                          name: 'Estimated Performance',
                          data: performanceChartData,
                          itemStyle: {
                              color: '#CE7553',
                          },
                      },
                  ]
                : []),
            {
                type: 'line',
                name: 'Event Duration',
                symbol: 'rect',
                z: 0,
                showSymbol: false,
                data: preparedEnergyData,
                markArea: {
                    data: eventChartData,
                    z: -1,
                    silent: true,
                },
                emphasis: {
                    showSymbol: false,
                    itemStyle: {
                        color: 'transparent',
                    },
                },
                lineStyle: {
                    width: 0,
                    color: '#F1D5C7',
                },
                itemStyle: {
                    color: '#F1D5C7',
                },
                tooltip: {
                    enabled: false,
                },
            },
        ],
        dataZoom: [
            {
                filterMode: 'weakFilter',
                type: 'inside',
                minSpan: 2,
            },
        ],
    };

    return (
        <Card style={{ marginBottom: '16px' }} className="energy-usage-widget">
            <Typography.Title level={4} style={{ marginTop: 0, color: SECONDARY_COLOR }}>
                Energy Demand Over Time
            </Typography.Title>
            {!showBaseline && baselineWarning && <Alert type="warning" showIcon style={{ marginBottom: '16px' }} message={baselineWarning} />}
            <div className="site-energy-chart-title">
                {isDataExist ? (
                    <ReactEChartsCore echarts={echarts} option={option} notMerge lazyUpdate />
                ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
            </div>
        </Card>
    );
};

enum EventTs {
    START = 1,
    INSIDE = 2,
    END = 3,
}

function makeEventsMap(eventsData: EnergyEventDto[]) {
    const ONE_MINUTE_MS = 60 * 1000;
    const eventsMap = new Map<number, EventTs>();

    eventsData.forEach(event => {
        const startMs = new Date(event.start).valueOf();
        const endMs = startMs + event.duration * ONE_MINUTE_MS;

        let cursor = startMs;
        while (cursor <= endMs) {
            let val = EventTs.INSIDE;
            if (cursor === startMs) val = EventTs.START;
            if (cursor === endMs) val = EventTs.END;

            eventsMap.set(cursor, val);
            cursor = cursor + ONE_MINUTE_MS;
        }
    });

    return eventsMap;
}
